/**
 * Hiding a column from particular people.
 *
 * Two different questions, and they need two endpoints because they have two
 * different answers:
 *
 * - **Who is a column hidden from?** Asked of one field, answered with a list
 *   of account ids, and replaced wholesale by a `PUT`. This is what somebody
 *   setting redaction up reads and writes.
 * - **Which columns are hidden from *me*?** Asked of a table, answered with a
 *   list of field ids. This is what the grid needs, because a redacted cell
 *   arrives as the literal string `[REDACTED]` -- and handing that to a date or
 *   a number reader draws a column that looks broken rather than one that looks
 *   withheld.
 *
 * The second is not derivable from the first: a reader who may not see a column
 * generally may not read who else cannot see it either.
 */
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { FieldRedaction } from '@/api/types'

/** How a column is governed: who it is hidden from, and whether AI may read it. */
export function fieldRedactionQuery(fieldId: number) {
  return queryOptions({
    queryKey: qk.fieldRedaction(fieldId),
    queryFn: (): Promise<FieldRedaction> =>
      unwrap(
        getApi().GET('/api/database/fields/{field_id}/redaction/', {
          params: { path: { field_id: fieldId } },
        }),
      ),
  })
}

export function useFieldRedaction(fieldId: number) {
  return useQuery(fieldRedactionQuery(fieldId))
}

/**
 * The ids of the fields of a table that are hidden from whoever is asking.
 *
 * Read once and kept, like the schema it is about: redaction is set rarely and
 * by somebody else, and nothing broadcasts a change to it. A reader whose
 * access changed under them sees it on their next load, which is the same deal
 * every other permission in this application offers.
 */
export function tableRedactionsQuery(tableId: number) {
  return queryOptions({
    queryKey: qk.tableRedactions(tableId),
    staleTime: Infinity,
    queryFn: async (): Promise<number[]> => {
      const body = await unwrap(
        getApi().GET('/api/database/fields/table/{table_id}/redactions/', {
          params: { path: { table_id: tableId } },
        }),
      )
      return body.redacted_field_ids
    },
  })
}

export function useTableRedactions(tableId: number) {
  return useQuery(tableRedactionsQuery(tableId))
}

/**
 * Replaces both halves of a column's governance in one write.
 *
 * A `PUT` and not a `PATCH`, which is the endpoint's own choice and the right
 * one: "these people" is a statement about all of them, and adding one at a
 * time would leave two tabs able to remove somebody by not mentioning them.
 * `mcp_access` rides along because one dialog and one Save set both.
 */
export function useSetFieldRedaction(tableId: number, fieldId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (redaction: FieldRedaction): Promise<FieldRedaction> =>
      unwrap(
        getApi().PUT('/api/database/fields/{field_id}/redaction/', {
          params: { path: { field_id: fieldId } },
          body: redaction,
        }),
      ),
    onSuccess: (answer) => {
      queryClient.setQueryData<FieldRedaction>(qk.fieldRedaction(fieldId), answer)
      // What *this* reader may see can have changed by it: redacting a column
      // from yourself is allowed, and the grid draws from that list.
      void queryClient.invalidateQueries({ queryKey: qk.tableRedactions(tableId) })
    },
  })
}
